import { Fragment, ReactNode, useEffect, useRef, useMemo } from 'react'
import { JSX } from 'react'

import { omit } from 'lodash'
import { usePathname } from 'next/navigation'

import { useSyncExtensionConnectionNetworks } from '~/app/_root/useSyncExtensionConnectionNetworks'
import { useSyncExtensionSessionInfo } from '~/app/_root/useSyncExtensionSessionInfo'
import { AddPasskeyDialog } from '~/components/AddPasskeyDialog'
import { GlobalModal } from '~/components/GlobalModal'
import { Layout } from '~/components/Layout'
import { LoadingIndicator } from '~/components/LoadingIndicator'
import { LoginModal } from '~/components/LoginModal'
import { useRecoveryAuthRestore } from '~/components/smart-accounts/useRecoveryAuthRestore'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg, useActiveOrgId } from '~/hooks/org'
import { useLatestAssetTransferNotifications } from '~/hooks/useAssetTransfers'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useNavigationTracker } from '~/hooks/useBackNavigation'
import { useStuckLoadingDetection } from '~/hooks/useStuckLoadingDetection'
import { useCookieStore } from '~/store/cookieStore'
import { sendTrackingData } from '~/utils/tracking'

export const AppContainer = ({
  children,
}: {
  children: ReactNode
}): JSX.Element => {
  const { initialLoadingState } = useAuthUser()
  const { loginModalState, closeGlobalModal, globalModalState } = useAuth()
  const { isLoading: isActiveOrgLoading } = useActiveOrg()
  const activeOrgId = useActiveOrgId()

  const pathname = usePathname()

  useEffect(() => {
    if (pathname) {
      sendTrackingData('pageview', { pathname })
    }
  }, [pathname])

  // Close global modal when navigating to a different page
  const prevPathnameRef = useRef(pathname)
  const globalModalStateRef = useRef(globalModalState)
  globalModalStateRef.current = globalModalState

  useEffect(() => {
    if (prevPathnameRef.current !== pathname) {
      prevPathnameRef.current = pathname
      // Skip close if this pathname change came from useDialogRoutePath
      // pushing or restoring a dialog path
      const historyState = window.history.state
      if (historyState?.dialogRoutePath) return
      if (historyState?.dialogRouteRestored) {
        // The restore wrote the flag onto the page's own history entry, so
        // consume it here: a later Back onto this entry is a real navigation.
        window.history.replaceState(
          omit(historyState, 'dialogRouteRestored'),
          '',
          window.location.href,
        )
        return
      }
      const { isOpen, onDismiss } = globalModalStateRef.current
      if (isOpen) {
        onDismiss?.()
        closeGlobalModal()
      }
    }
  }, [pathname, closeGlobalModal])

  // Restore transaction dialog after recovery signer Privy auth completes
  useRecoveryAuthRestore()

  // polls server for latest asset transfers to notify user
  useLatestAssetTransferNotifications()

  // Mirrors the signed-in user to the splits-connect extension popup
  useSyncExtensionSessionInfo()

  // Mirrors the active org's enabled networks per connected site to the
  // splits-connect extension popup
  useSyncExtensionConnectionNetworks()

  // Track navigation history for back navigation
  useNavigationTracker()

  const memoizedChildren = useMemo(
    () => (
      <>
        {/* The key prop here forces a remount on open/close to reset internal state */}
        <LoginModal key={loginModalState ? 'open' : 'closed'} />
        <GlobalModal />
        <AddPasskeyDialog />
        {activeOrgId ? (
          <Fragment key={activeOrgId}>{children}</Fragment>
        ) : (
          children
        )}
      </>
    ),
    [children, loginModalState, activeOrgId],
  )

  const isShowingLoadingIndicator =
    initialLoadingState || (isActiveOrgLoading && activeOrgId)
  const authToken = useCookieStore((s) => s.authToken)

  useStuckLoadingDetection('AppContainer', !!isShowingLoadingIndicator, {
    extra: {
      initialLoadingState,
      isActiveOrgLoading,
      activeOrgId,
      hasAuthToken: !!authToken,
    },
  })

  // Do not show loading indicator when activeOrg is loading if activeOrgId is not set
  // Applicable for accepting payee invite
  if (isShowingLoadingIndicator) {
    return <LoadingIndicator />
  }

  return <Layout>{memoizedChildren}</Layout>
}
